import { useReducer, useEffect, useRef, useContext, type Reducer } from 'react';
import axios, { type CancelTokenSource } from 'axios';
import _isEqual from 'lodash/isEqual';

import { AuthState } from 'services/auth';
import { API_URL } from 'consts';
import { usePrevious } from 'utils';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface FetchState<TResult> {
  loading: boolean;
  error: unknown;
  results: TResult | null;
  reqSource?: CancelTokenSource;
}

type FetchAction<TResult> =
  | { type: 'FETCH_START'; reqSource: CancelTokenSource }
  | { type: 'FETCH_SUCCESS'; results: TResult }
  | { type: 'FETCH_CANCELLED' }
  | { type: 'FETCH_FAIL'; error: unknown }
  | { type: 'CACHE_HIT'; data: TResult };

const initialState: FetchState<unknown> = {
  loading: false,
  error: null,
  results: null,
};

function reducer<TResult>(
  state: FetchState<TResult>,
  action: FetchAction<TResult>,
): FetchState<TResult> {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, results: null, error: null, reqSource: action.reqSource };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        results: action.results,
        loading: false,
        error: null,
      };
    case 'FETCH_CANCELLED':
      return { ...state, loading: false, error: null };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.error };
    case 'CACHE_HIT':
      return { results: action.data, loading: false, error: null };
    default:
      return state;
  }
}

export const useAxiosConfig = () => {
  const { access_token } = useContext(AuthState);

  const config = {
    baseURL: API_URL,
    headers: {
      authorization: `Bearer: ${access_token}`,
    },
  };

  return { axios, config };
};

export const useFetch = <TResult = unknown, TData = unknown>(
  uri: string | null,
  runOnMount = true,
  method: HttpMethod = 'GET',
  params: Record<string, unknown> = {},
) => {
  const { access_token } = useContext(AuthState);
  const [state, dispatch] = useReducer<Reducer<FetchState<TResult>, FetchAction<TResult>>>(
    reducer,
    initialState as FetchState<TResult>,
  );
  const isMounted = useRef(true);
  const previousParams = usePrevious(params);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetch = (data: TData | null = null) => {
    if (!uri) {
      return Promise.resolve(undefined);
    }

    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    dispatch({ type: 'FETCH_START', reqSource: source });

    return axios({
      method,
      url: `${API_URL}${uri}`,
      params,
      data,
      headers: {
        authorization: `Bearer: ${access_token}`,
      },
      cancelToken: source.token,
    })
      .then((res) => {
        if (!isMounted.current) {
          return undefined;
        }

        dispatch({ type: 'FETCH_SUCCESS', results: res.data as TResult });
        return res;
      })
      .catch((err) => {
        if (!isMounted.current) {
          return undefined;
        }

        if (!axios.isCancel(err)) {
          dispatch({ type: 'FETCH_FAIL', error: err });
        }

        return undefined;
      });
  };

  useEffect(() => {
    if (_isEqual(previousParams, params)) {
      return;
    }

    if (runOnMount) {
      void fetch();
    }
    // eslint-disable-next-line
  }, [uri, method, params]);

  return { ...state, fetch };
};
